import {
	Component,
	OnInit,
	Input,
	ViewChild,
	ElementRef,
	OnChanges,
	SimpleChanges,
	OnDestroy
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription, Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { camelCase, toNumber } from 'lodash';
// Models
import { SchemasI } from '../models/schemas.interface';
import { MapViewConfigModel } from '@cv-invision/cv.component-library.ux';
// States
import { QualityState } from '../../state/quality.state';
// Services
import { BroadcastService } from '@cv-invision/core';
import { JPathService } from '@cv-invision/cv.component-library.ux';
import { QualityDocumentService } from '../../service/document-quality.service';
// Components
import { HISTORICAL_VALUE_EVENT } from '../../historical-values/historical-values.component';
import { RulesState } from '../../rules/state/rules.state';
import { RuleModel } from '../../rules/model/rule.model';
import { UI_FIELD_SELECTED_EVENT } from '../../rules/rules.component';

@Component({
	selector: 'cv-in-review-fields',
	templateUrl: './in-review-fields.component.html',
	styleUrls: ['./in-review-fields.component.scss']
})
export class InReviewFieldsComponent implements OnInit, OnChanges, OnDestroy {
	rules$: Observable<any> = this.store.select(RulesState.getOrderRules);
	@ViewChild('myField', { read: ElementRef, static: false }) myField: ElementRef;
	@ViewChild('changesEl', { read: ElementRef, static: false }) changesEl: ElementRef;
	@Input() options: any;
	@Input() targets = [];
	@Input() highlight = '';
	@Input() orderId = null;
	@Input() selectedSection = '';

	private atComponentDestroy = new Subject();
	public mapConfiguration = new MapViewConfigModel();

	active = false;
	document: any;
	schema: SchemasI;
	field: any;
	value: any;
	revisedFields: any;
	historical = [];

	paths = {
		original: '',
		jsonPath: '',
		schema: '',
		path: ''
	};

	show = {
		value: true,
		label: true
	};

	gps = {
		lat: 0,
		long: 0
	};

	iconExist = false;
	icon = '';
	icons = {
		stable: 'fa fa-arrows-alt-h',
		decreasing: 'fa fa-arrow-circle-down',
		declining: 'fa fa-arrow-circle-down',
		slow: 'fa fa-arrow-circle-down',
		increasing: 'fa fa-arrow-circle-up',
		rapid: 'fa fa-arrow-circle-up',
		yes: 'fa fa-check',
		no: 'fa fa-times',
		avg: 'fa fa-star-half-alt',
		gd: 'fa fa-star',
		good: 'fa fa-star',
		average: 'fa fa-star-half-alt',
		poor: 'far fa-star'
	};

	serviceSubscription: Subscription;

	public rules: any;

	constructor(
		private store: Store,
		private broadcastService: BroadcastService,
		private qualityService: QualityDocumentService,
		private jPathService: JPathService
	) { }

	ngOnInit(): void {
		this.document = this.store.selectSnapshot(QualityState.getOrderDocument);
		this.schema = this.store.selectSnapshot(QualityState.geSchemaDocument) as SchemasI;

		this.rules$.pipe(takeUntil(this.atComponentDestroy)).subscribe((orderRules: any) => {
			this.rules = orderRules;
		});

		if ('show' in this.options) {
			Object.assign(this.show, this.options.show);
		}

		if ('path' in this.options) {
			Object.keys(this.paths).forEach(key => {
				this.paths[key] = this.options.path;
			});

			const pathPattern = /(\[[a-zA-z=0-9'\\]+\])/g;
			const matches = this.paths.path.match(pathPattern);

			if (matches) {
				const jsonPath = this.paths.path.replace(pathPattern, match => {
					const splitedValues = match.replace(/[[\]']+/g, '').split('=');
					const jsonPathString = '[?(@.' + splitedValues[0] + '.Value==' + splitedValues[1] + ')]';

					return jsonPathString;
				});

				const schema = this.paths.path.replace(pathPattern, '');

				this.paths.original = this.options.path;
				this.paths.jsonPath = jsonPath;
				this.paths.schema = schema;
			}

			let field;
			if ('InspectionReport' in this.schema) {
				field = this.jPathService.getValue(this.document, `$.${this.paths.path}`);
			} else {
				field = this.jPathService.getValue(this.document, `$.${this.paths.jsonPath}`);
			}

			const jpField = this.jPathService.getValue(this.schema, `$.${this.paths.schema}.Metadata[0]`);

			if (jpField) {
				this.field = JSON.parse(
					JSON.stringify(jpField)
				);
			} else {
				this.field = {};
				this.show.label = false;
				this.show.value = false;
			}

			if (field) {
				this.value = field.Value;
				if ('InspectionReport' in this.schema) {
					this.value = this.getSchemaValue(this.paths.schema, field.Value);
				}
				// this.field = JSON.parse(JSON.stringify(field.Metadata[0]));
				if (field.Historical.length > 0) {
					this.historical = this.formatHistorical(field.Historical);
				}
			} else {
				this.value = '--';
				// this.schema = selectedSchema;
				// this.field = JSON.parse(JSON.stringify(this.jPathService.getValue(this.schema, `$.${this.paths.schema}.Metadata[0]`)));
			}

			if (typeof this.value === 'string' && this.value.toLocaleLowerCase() in this.icons) {
				this.icon = this.value.toLocaleLowerCase();
				this.iconExist = true;
			}

			if (typeof this.value === 'string' && this.value !== '') {
				this.value = this.value.replace(/([a-z])([A-Z])/g, '$1 $2');
				this.value = this.value.replace(/([a-z])([1-9])/g, '$1 $2');
				this.value = this.value.replace(/([1-9])([A-Z])/g, '$1 $2');
			}
		}

		if ('label' in this.options) {
			const newLabels = {
				UILabel: this.options.label,
				DisplayText: this.options.label
			};
			Object.assign(this.field, newLabels);
		}

		if ('displayType' in this.options && this.options.displayType === 'map') {
			if ('address' in this.options) {
				const addressArray = [
					this.jPathService.getValue(this.document, `$.${this.options.address[0]}`).Value,
					this.jPathService.getValue(this.document, `$.${this.options.address[1]}`).Value,
					this.jPathService.getValue(this.document, `$.${this.options.address[2]}`).Value
				];

				this.serviceSubscription = this.qualityService
					.getOrderCoordinates(addressArray.join(','))
					.subscribe(response => {
						if (response.valid) {
							this.gps.lat = response.latitude;
							this.gps.long = response.longitude;
							this.mapConfiguration.setMainLocation({ latitude: this.gps.lat, longitude: this.gps.long });
						}
					});
			} else {
				this.gps.lat = parseFloat(this.jPathService.getValue(this.document, `$.${this.options.gps}`).latitude.Value);
				this.gps.long = parseFloat(
					this.jPathService.getValue(this.document, `$.${this.options.gps}`).longitude.Value
				);
				this.mapConfiguration.setMainLocation({ latitude: this.gps.lat, longitude: this.gps.long });
			}
			this.mapConfiguration.setOptions({ mapTypeId: 'roadmap' })
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if ('targets' in changes && !changes.targets.firstChange) {
			this.active = this.targets.indexOf(this.paths.original) > -1;
			if (this.active) {
				this.myField.nativeElement.scrollIntoView(
					window.innerHeight < 750 ? false : { behavior: 'smooth', block: 'start' }
				);
			}
		}
	}

	/**
	 * Select field
	 */
	onFieldSelected(): void {
		if (this.paths && this.paths.path) {
			let rules = this.getFieldRules(this.paths.path);
			if (rules.length) {
				rules = rules.filter(r => r.uiSection.toLocaleLowerCase() === this.selectedSection);
			}
			if (rules.length) {
				this.broadcastService.broadcast({
					name: UI_FIELD_SELECTED_EVENT,
					event: {
						rule: rules[0]
					}
				});
			}
		}
	}

	/**
	 * The field has a rule asigned
	 */
	fieldHasRule(): boolean {
		if (this.paths && this.paths.path) {
			let rules = this.getFieldRules(this.paths.path);
			if (rules.length) {
				rules = rules.filter(r => r.uiSection.toLocaleLowerCase() === this.selectedSection);
			}
			return rules.length > 0;
		}

		return false;
	}

	/**
	 * Return the rules asigned to the field
	 * @param fieldPath
	 */
	getFieldRules(fieldPath: string): Array<RuleModel> {
		const field = fieldPath.replace(/[^a-zA-Z ]/g, '').toLowerCase();
		const rules: Array<RuleModel> = this.rules.violation.filter(rule => {
			let isAvailable = false;
			if (rule.targets.length) {
				const targets = rule.targets
					.join()
					.replace(/[^a-zA-Z ]/g, '')
					.toLowerCase();
				isAvailable = targets.indexOf(field) !== -1;
			}
			return isAvailable;
		});

		return rules;
	}

	ngOnDestroy(): void {
		if (this.serviceSubscription) {
			this.serviceSubscription.unsubscribe();
		}
	}

	/**
	 * open historical component
	 * @param item
	 * @param historical
	 */
	async historicalVal(historical: Array<any>): Promise<void> {
		const { height, left, width, top } = await this.changesEl.nativeElement.getBoundingClientRect();
		const offsetHeight = height;
		const offsetLeft = left - width / 2;
		const offsetTop = top;
		const offsetWidth = width;
		const scrollTop = 0;
		this.broadcastService.broadcast({
			name: HISTORICAL_VALUE_EVENT,
			event: {
				isOpen: true,
				data: historical,
				pos: { offsetHeight, offsetLeft, offsetTop, offsetWidth, scrollTop }
			}
		});
	}

	/**
	 * asdasd
	 *
	 * @param value
	 */
	parseNumeric(value: any): any {
		const newVal = toNumber(value.replace(/,/g, ''));
		return newVal;
	}

	/**
	 * isNumber
	 *
	 * @param val
	 */
	isNumber(value: any): boolean {
		const test = parseFloat(value);
		return typeof test === 'number' && !isNaN(test);
	}

	/**
	 * formatHistorical
	 *
	 * @param historical
	 */
	formatHistorical(historical: Array<any>): Array<any> {
		const formated = [];
		historical.forEach(item => {
			const groupParse: any = [];
			Object.keys(item).forEach(key => {
				const nKey = camelCase(key);
				groupParse[nKey] = item[key];
			});
			formated.push(Object.assign({}, groupParse));
		});

		return formated;
	}

	/**
	 * getSchemaValue
	 *
	 * @param path asd
	 * @param term asd
	 */
	getSchemaValue(path: string, term: string): any {
		const fullPath = `${path}.Metadata[0].Enumeration..[?(@.Key == "${term}")]`;
		const search = this.jPathService.getQuery(this.schema, fullPath) || [];
		if (search.length > 0) {
			return search[0].Value;
		}
		return term;
	}
}
